import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI } from '@api';
import { projectAtom } from '@data/auth';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useNotification } from '@data/notification';
import { useTranslation } from 'react-i18next';
import type { Vehicle } from '@data/fms/vehicle/types';
import { overrideTelemetryEmergencyStatus } from '@data/fms/vehicle/utils';
import { useAtomCallback } from 'jotai/utils';

/**
 * プロジェクト内の車両一覧取得API
 */
export const useProjectVehiclesAPI = (): {
  state: APIState;
  getProjectVehicles: () => Promise<Vehicle[]>;
} => {
  const [state, setState] = useState<APIState>('none');
  const fmsAPI = useFMSAPI();
  const { notifyError } = useNotification();
  const { t } = useTranslation();

  const getVehiclesAPI = useCallback(
    async (projectId: string): Promise<Result<Vehicle[], AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(`/${projectId}/vehicles`);
        if (isNullOrUndefined(res?.data)) return new Success([]);
        // 昇順ソート
        res?.data?.sort((a: Vehicle, b: Vehicle) => {
          if (a.vehicle_name < b.vehicle_name) return -1;
          return 1;
        });
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getProjectVehicles = useAtomCallback(
    useCallback(
      async (get) => {
        const project = get(projectAtom);
        if (isNullOrUndefined(project)) {
          notifyError({
            message: t('api.fms.get_vehicles', { status: 'failed' }),
          });
          setState('hasError');
          return [];
        }
        setState('loading');
        const res = await getVehiclesAPI(project.id);
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          setState('hasError');
          return [];
        }
        setState('hasValue');
        const newRes = res.value.map((vehicle: Vehicle) => {
          if (vehicle.telemetry.status === 'shutdown') {
            overrideTelemetryEmergencyStatus(vehicle.telemetry);
          }
          return vehicle;
        });
        return newRes;
      },
      [getVehiclesAPI, notifyError, t],
    ),
  );

  return {
    state,
    getProjectVehicles,
  };
};
